import { useState } from 'react';

/** Reset targets accepted by `trpc.lineUser.reset`. Keep in sync with backend. */
export type ResetTarget =
  | 'lineUserState'
  | 'conversationHistory'
  | 'messageSendLog'
  | 'eventCreateLog'
  | 'sfAccount'
  | 'sqsDlq';

interface TargetItem {
  key: ResetTarget;
  label: string;
}

const TARGETS: readonly TargetItem[] = [
  { key: 'lineUserState', label: 'LINEユーザー状態 (LineUserState)' },
  { key: 'conversationHistory', label: '会話履歴 (ConversationHistory)' },
  { key: 'messageSendLog', label: 'メッセージ送信ログ (MessageSendLog)' },
  { key: 'eventCreateLog', label: '行動作成ログ (EventCreateLog)' },
  { key: 'sfAccount', label: 'Salesforce 紐付け情報 (LineUserId / 進捗ステータス)' },
  { key: 'sqsDlq', label: 'SQS DLQ メッセージ (再配信予定の webhook)' },
] as const;

interface ResetDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (targets: ResetTarget[]) => void;
}

/**
 * Destructive-reset confirmation modal. Operator picks one or more
 * tables / queues to wipe for a single LINE userId. In production the
 * operator must additionally type `prod` to unlock the OK button — a
 * deliberate second confirmation gate per spec §8.3.
 *
 * Defaults: all targets selected (UAT operators typically want full reset).
 */
export function ResetDialog({ open, onClose, onConfirm }: ResetDialogProps): JSX.Element | null {
  const env = (import.meta.env.VITE_ENV_NAME as string | undefined) ?? 'dev';
  const isProd = env === 'prod';
  const [selected, setSelected] = useState<Set<ResetTarget>>(
    () => new Set(TARGETS.map((t) => t.key)),
  );
  const [typed, setTyped] = useState('');

  if (!open) return null;

  const hasSelection = selected.size > 0;
  const prodGateOk = !isProd || typed === 'prod';
  const okEnabled = hasSelection && prodGateOk;

  const toggle = (t: ResetTarget) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t);
      else next.add(t);
      return next;
    });
  };

  const handleConfirm = () => {
    if (!okEnabled) return;
    const ordered = TARGETS.filter((t) => selected.has(t.key)).map((t) => t.key);
    onConfirm(ordered);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="reset-dialog-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
    >
      <div className="w-full max-w-md rounded bg-white p-5 shadow-lg">
        <h2 id="reset-dialog-title" className="text-lg font-bold text-gray-900">
          データをリセット
        </h2>
        {isProd ? (
          <div className="mt-2 rounded bg-red-600 px-2 py-1 text-xs font-bold uppercase text-white">
            DESTRUCTIVE — PRODUCTION
          </div>
        ) : null}
        <p className="mt-2 text-xs text-gray-600">
          このユーザーに対して削除する対象を選択してください。
        </p>
        <fieldset className="mt-3 space-y-2">
          <legend className="sr-only">リセット対象</legend>
          {TARGETS.map((t) => (
            <label key={t.key} className="flex items-start gap-2 text-sm text-gray-800">
              <input
                type="checkbox"
                checked={selected.has(t.key)}
                onChange={() => toggle(t.key)}
                aria-label={t.key}
                className="mt-1"
              />
              <span>{t.label}</span>
            </label>
          ))}
        </fieldset>
        {isProd ? (
          <div className="mt-4">
            <label htmlFor="reset-confirm-type" className="block text-xs text-gray-700">
              確認のため <span className="font-mono font-bold">prod</span> と入力してください:
            </label>
            <input
              id="reset-confirm-type"
              type="text"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              className="mt-1 w-full rounded border border-gray-300 px-2 py-1 font-mono text-sm focus:border-gray-900 focus:outline-none"
            />
          </div>
        ) : null}
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-gray-300 px-3 py-1 text-sm text-gray-700 hover:bg-gray-50"
          >
            キャンセル
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!okEnabled}
            className="rounded bg-red-600 px-3 py-1 text-sm text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            リセット実行
          </button>
        </div>
      </div>
    </div>
  );
}
